import { useCallback } from 'react';

import {
  buildLegendTiles,
  LegendTileProps,
  LegendTilesConfig,
  TooltipCallbackParams,
} from '@dbt-labs/charts';
import { RyeconDuotoneWarn } from '@dbt-labs/sourdough';

import { CostInsight } from '../../typings/costInsights';

export const legendTilesBuilder = ({
  config,
  value,
  label,
  color = 'rgb(11, 165, 139, 0.2)',
}: {
  config: LegendTilesConfig;
  value: number;
  label: string;
  color: string;
}) => {
  const seriesTiles = buildLegendTiles(config);
  const areaTile: LegendTileProps = {
    label,
    color,
    value,
    formatValue: config.formatValue,
    isInteractive: false,
  };
  return [...seriesTiles, areaTile];
};

/**
 * Creates a legend tile for empty state display.
 * All empty state tiles use '--' as the formatted value to indicate no data.
 *
 * @param label - The label text for the legend tile
 * @param color - The color for the legend tile (CSS variable or color value)
 * @returns A legend tile object configured for empty state display
 */
export const createEmptyLegendTile = (
  label: string,
  color: string,
): LegendTileProps => {
  return {
    value: 0,
    label,
    color,
    formatValue: () => '--',
  };
};

/**
 * Hook that returns a tooltip header callback showing a warning
 * when cost data is still being processed (partial data).
 */
export const usePartialDataTooltipHeader = (groupedData: CostInsight[]) => {
  return useCallback(
    (_params: TooltipCallbackParams, formattedDate: string) => {
      const dataIndex = (_params[0] as any)?.dataIndex;
      if (dataIndex != null && groupedData[dataIndex]?.isCostProcessed === false) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <RyeconDuotoneWarn style={{ color: 'var(--bgDuotoneWarning)' }} />
              <span style={{ fontWeight: 500 }}>{formattedDate}</span>
            </div>
            <span style={{ color: 'var(--fgAlt)', fontSize: '12px' }}>
              Displaying partial data from your warehouse.{' '}
              <a
                href="https://docs.getdbt.com/docs/explore/cost-insights#related-faqs"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline' }}
              >
                Learn more
              </a>
            </span>
          </div>
        );
      }
      return null;
    },
    [groupedData],
  );
};
